import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getListCalendarConnectionsQueryKey } from '@biasafe.queries/calendar-connections/calendar-connections';
import { useConnectGoogleCalendar } from '@biasafe.queries/calendar-google-connect/calendar-google-connect';
import { useConnectMicrosoftCalendar } from '@biasafe.queries/calendar-microsoft-connect/calendar-microsoft-connect';
import { requestCalendarCode } from './connectFlows';
import type { CalendarProvider } from './connectFlows';

/**
 * The calendar connect action: popup consent, then the server-side exchange.
 *
 * EXTRACTED FROM `CalendarConnectionsCard` so the Settings card, and the
 * "Connect your tools" step that follows sign-up, run ONE flow. Nothing about
 * the OAuth round trip lives here: `requestCalendarCode` opens the popup against
 * the URL the API minted, and the connect mutation hands the code back for the
 * API to exchange. This only sequences the two and owns the busy and error
 * state the callers draw.
 *
 * `connect` resolves to whether the calendar is now connected, and never throws:
 * every failure is turned into `error`, a sentence already written for a person
 * (a blocked popup, a cancelled sign-in, an unconfigured provider), with a
 * generic fallback for the API's deliberately terse ones.
 */
export function useConnectCalendar() {
  const queryClient = useQueryClient();
  const connectGoogle = useConnectGoogleCalendar();
  const connectMicrosoft = useConnectMicrosoftCalendar();
  // Which provider's popup is open. Not the mutation's own isPending: the
  // popup is open for most of the wait and no mutation has started yet.
  const [pending, setPending] = useState<CalendarProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const connect = async (provider: CalendarProvider): Promise<boolean> => {
    setPending(provider);
    setError(null);
    try {
      const { code, state, redirectUri } = await requestCalendarCode(provider);
      const body = { code, state, redirect_uri: redirectUri };
      if (provider === 'GOOGLE') {
        await connectGoogle.mutateAsync({ data: body });
      } else {
        await connectMicrosoft.mutateAsync({ data: body });
      }
      await queryClient.invalidateQueries({
        queryKey: getListCalendarConnectionsQueryKey(),
      });
      toast.success('Calendar connected.');
      return true;
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Could not connect that calendar.';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setPending(null);
    }
  };

  return { connect, pending, error, setError };
}
